import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import { ArrowSquareOut } from "@phosphor-icons/react/ArrowSquareOut";

import {
	ColumnDef,
	DataTable,
} from "~ui-components/components/atoms/data-table";
import { dayjs } from "~ui-components/libs/dayjs";
import {
	Account,
	AccountRecurlyInvoice,
	RECURLY_INVOICE_STATE,
} from "~ui-components/types/__generated/gql/graphql";
import { getRecurlyHostedInvoiceUrl } from "~utils/recurly/get-recurly-hosted-invoice-url";

export interface InvoicesTableProps {
	account: Account;
	rows: AccountRecurlyInvoice[];
}

export function InvoicesTable({ account, rows }: InvoicesTableProps) {
	const columns = [
		{
			formatter: (row) => dayjs(row.createdAt).format("MMM D, YYYY"),
			field: "createdAt",
			name: "Created At",
			width: "250px",
		},
		{
			formatter: (row) => {
				if (
					row.state === RECURLY_INVOICE_STATE.past_due ||
					row.state === RECURLY_INVOICE_STATE.pending
				)
					return (
						<Button
							variant="contained"
							color="primary"
							href={getRecurlyHostedInvoiceUrl({
								invoiceId: row.number,
								token: account._recurlyAccountInfo?.hostedToken ?? "",
							})}
							target="_blank"
							endIcon={<ArrowSquareOut />}>
							Pay now
						</Button>
					);

				return (
					<Button
						variant="text"
						color="secondary"
						href={getRecurlyHostedInvoiceUrl({
							invoiceId: row.number,
							token: account._recurlyAccountInfo?.hostedToken ?? "",
						})}
						target="_blank"
						endIcon={<ArrowSquareOut />}>
						Invoice
					</Button>
				);
			},
			name: "",
			align: "right",
			width: "150px",
		},
		{
			formatter: (row) => `#${row.number}`,

			name: "Invoice",
			width: "150px",
			align: "right",
		},
		{
			formatter: (row) => {
				if (row.state === RECURLY_INVOICE_STATE.paid)
					return (
						<Chip
							color="success"
							label="Paid"
							size="small"
							variant="soft"
						/>
					);
				if (row.state === RECURLY_INVOICE_STATE.past_due)
					return (
						<Chip
							color="error"
							label="Past due"
							size="small"
							variant="soft"
						/>
					);

				return (
					<Chip
						color="warning"
						label={row.state}
						size="small"
						variant="soft"
					/>
				);
			},
			name: "Status",
			width: "150px",
			align: "center",
		},
		{
			formatter: (row) =>
				new Intl.NumberFormat("en-US", {
					style: "currency",
					currency: "USD",
				}).format(row.total),
			name: "Amount",
			width: "150px",
			align: "right",
		},
	] satisfies ColumnDef<AccountRecurlyInvoice>[];

	return (
		<DataTable<AccountRecurlyInvoice>
			columns={columns}
			rows={rows}
		/>
	);
}
